import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Package, Plus, Download, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  Draft:    { bg: 'bg-slate-100 text-slate-700 border-slate-200',       label: 'Draft' },
  Issued:   { bg: 'bg-blue-100 text-blue-800 border-blue-200',          label: 'Issued' },
  Shipped:  { bg: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Shipped' },
  Closed:   { bg: 'bg-slate-900 text-white border-slate-900',           label: 'Closed' },
  Canceled: { bg: 'bg-red-100 text-red-800 border-red-200',             label: 'Canceled' },
};

const PackingSlipsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [slips, setSlips] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (customerFilter !== 'all') params.customer_id = customerFilter;
      const qs = new URLSearchParams(params).toString();
      const [ps, c] = await Promise.all([
        api().get(`/packing-slips${qs ? `?${qs}` : ''}`),
        api().get('/customers'),
      ]);
      setSlips(ps.data || []);
      setCustomers(c.data || []);
    } catch { toast.error('Failed to load packing slips'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, customerFilter]);

  const bulkCsv = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (customerFilter !== 'all') params.customer_id = customerFilter;
      const qs = new URLSearchParams(params).toString();
      const res = await api().get(`/packing-slips/export.csv${qs ? `?${qs}` : ''}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `packing-slips-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bulk CRM export downloaded');
    } catch { toast.error('Bulk export failed'); }
  };

  const filtered = slips.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.ps_number || '').toLowerCase().includes(q)
      || (s.customer_name_snapshot || '').toLowerCase().includes(q)
      || (s.source_number_snapshot || '').toLowerCase().includes(q)
      || (s.tracking_number || '').toLowerCase().includes(q)
      || (s.carrier || '').toLowerCase().includes(q);
  });

  const stats = slips.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-4" data-testid="packing-slips-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Package className="w-7 h-7 text-slate-700" /> Packing Slips</h1>
          <p className="text-sm text-slate-500 mt-1">What left the dock, serial by serial. Spawn from a Sales or Production Order, or build standalone.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={bulkCsv} className="rounded-sm" data-testid="ps-bulk-csv-btn">
            <Download className="w-4 h-4 mr-1" /> Bulk CRM Export
          </Button>
          <Button onClick={() => navigate('/orders/packing/new')} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="ps-create-btn">
            <Plus className="w-4 h-4 mr-1" /> New Packing Slip
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-2">
        {['Draft', 'Issued', 'Shipped', 'Closed', 'Canceled'].map(k => (
          <Card key={k} className="industrial-card">
            <CardContent className="p-3 text-center">
              <div className="text-2xl font-bold" data-testid={`ps-stat-${k.toLowerCase()}`}>{stats[k] || 0}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{k}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-lg">All Packing Slips ({filtered.length})</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative w-56">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PS#, customer, tracking…" className="pl-8 rounded-sm" data-testid="ps-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 rounded-sm" data-testid="ps-status-filter"><Filter className="w-3.5 h-3.5 mr-1" /><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.keys(STATUS_META).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger className="w-48 rounded-sm" data-testid="ps-customer-filter"><SelectValue placeholder="Customer…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.customer_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-semibold">No Packing Slips yet</p>
              <p className="text-xs mt-1">Create one from a <Link to="/orders/sales" className="underline hover:text-slate-900">Sales Order</Link>, a <Link to="/orders/production" className="underline hover:text-slate-900">Production Order</Link>, or start standalone.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PS #</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Ship Date</TableHead>
                  <TableHead>Carrier</TableHead>
                  <TableHead>Tracking #</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Lines</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(s => {
                  const m = STATUS_META[s.status] || STATUS_META.Draft;
                  return (
                    <TableRow key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/orders/packing/${s.id}`)} data-testid={`ps-row-${s.ps_number}`}>
                      <TableCell className="font-semibold mono">{s.ps_number}</TableCell>
                      <TableCell>{s.customer_name_snapshot}</TableCell>
                      <TableCell className="text-xs">
                        {s.source_number_snapshot
                          ? <span className="mono"><Badge variant="outline" className="rounded-sm mr-1 text-[10px]">{s.source_type}</Badge>{s.source_number_snapshot}</span>
                          : <span className="text-slate-400">standalone</span>}
                      </TableCell>
                      <TableCell className="text-xs">{s.ship_date}</TableCell>
                      <TableCell className="text-xs">{s.carrier || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell className="text-xs mono">{s.tracking_number || <span className="text-slate-400">—</span>}</TableCell>
                      <TableCell><Badge className={`${m.bg} border rounded-sm text-xs`}>{m.label}</Badge></TableCell>
                      <TableCell className="text-right text-xs">{(s.lines || []).length}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PackingSlipsPage;
